import axios from 'axios';

const instance = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:2083',
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Guard flags: make sure 402 / 401 are handled only ONCE
let isHandlingExpired = false;
let isHandlingUnauthorized = false;

const clearAuth = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('token');
    localStorage.removeItem('authUser');
};

// ---------- Request interceptor: attach token ----------
instance.interceptors.request.use(
    (config) => {
        // Once we're redirecting, don't send any more requests
        if (isHandlingExpired || isHandlingUnauthorized) {
            return Promise.reject(new axios.Cancel('redirecting'));
        }

        const token = localStorage.getItem('authToken') || localStorage.getItem('token');
        if (token) {
            const cleanToken = token.startsWith('Bearer ') ? token.slice(7) : token;
            config.headers.Authorization = `Bearer ${cleanToken}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// ---------- Response interceptor ----------
instance.interceptors.response.use(
    (response) => response,
    (error) => {
        // Ignore our own cancelled requests
        if (axios.isCancel(error)) {
            return Promise.reject(error);
        }

        const originalRequest = error.config || {};
        const status = error.response?.status;
        const data = error.response?.data || {};
        const code = data.code;

        // ---------- 402 / CLIENT_EXPIRED (handled once) ----------
        if (status === 402 || code === 'CLIENT_EXPIRED') {
            if (!isHandlingExpired) {
                isHandlingExpired = true;

                sessionStorage.setItem(
                    'subscriptionMessage',
                    data.message || 'Your subscription has expired. Please renew your plan.'
                );

                // Remove this block if you want to keep the session
                clearAuth();

                window.location.href = '/signin?message=subscription-expired';
            }
            error.isSubscriptionExpired = true; // lets callers skip their own toasts
            return Promise.reject(error);
        }

        // ---------- 401 (handled once) ----------
        if (status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            if (!isHandlingUnauthorized) {
                isHandlingUnauthorized = true;
                clearAuth();

                const errorMessage = data.message || data.error || '';
                const isInactive =
                    errorMessage.toLowerCase().includes('inactive') ||
                    data.status === 'inactive';

                window.location.href = isInactive
                    ? '/signin?message=account_inactive'
                    : '/signin?message=session_expired';
            }
        }

        return Promise.reject(error);
    }
);

export default instance;